import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { fetchScheduleEntries } from '../../../services/cabScheduleService';
import { CAB_REPEAT_OPTIONS, getRepeatLabel } from '../../../constants/cabScheduleOptions';
import { useDateFormatter } from '../../../hooks/useDateFormatter';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

interface ScheduleEntriesTabProps {
  cabDefinitionId: string;
}

const ScheduleEntriesTab: React.FC<ScheduleEntriesTabProps> = ({ cabDefinitionId }) => {
  const { formatDate, formatTime } = useDateFormatter();
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [filterRepeats, setFilterRepeats] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['cab-schedule-entries', cabDefinitionId, page, pageSize, appliedSearch, filterRepeats, filterActive],
    queryFn: () =>
      fetchScheduleEntries({
        cabDefinitionId,
        page,
        pageSize,
        search: appliedSearch || undefined,
        repeats: filterRepeats || undefined,
        active: filterActive || undefined,
      }),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const formatDateTime = (iso: string) => {
    if (!iso) return '—';
    return `${formatDate(iso)} ${formatTime(iso)}`;
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
    setPage(1);
  };

  return (
    <div className="flex flex-col min-h-[320px]">
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search"
              className="pl-8 pr-3 py-1.5 w-48 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <select
            value={filterRepeats}
            onChange={(e) => {
              setFilterRepeats(e.target.value);
              setPage(1);
            }}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
            aria-label="Filter by repeats"
          >
            <option value="">All Repeats</option>
            {CAB_REPEAT_OPTIONS.map((o) => (
              <option key={o.value || 'none'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={filterActive}
            onChange={(e) => {
              setFilterActive(e.target.value);
              setPage(1);
            }}
            className="text-sm border border-gray-300 rounded-md px-2 py-1.5"
            aria-label="Filter by active"
          >
            <option value="">All</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button type="submit" className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            Search
          </button>
        </form>
        <Link
          to={`/admin/cab-meeting-management/cab-definitions/${cabDefinitionId}/schedule-entry/new`}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          New
        </Link>
      </div>

      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading schedule entries...</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <p className="text-sm font-medium">No records to display.</p>
            <p className="text-xs mt-1">Create a schedule entry to generate CAB meetings.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start Date &amp; Time</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">End Date &amp; Time</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Repeats</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm">
                    <Link
                      to={`/admin/cab-meeting-management/cab-definitions/${cabDefinitionId}/schedule-entry/${entry.id}/edit`}
                      className="text-indigo-600 font-medium hover:text-indigo-800 hover:underline"
                    >
                      {entry.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-900">{formatDateTime(entry.startDateTime)}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{formatDateTime(entry.endDateTime)}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{getRepeatLabel(entry.repeats)}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{entry.active ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <div className="px-4 py-2 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span>Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1"
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <span>
              {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
            </span>
            {isFetching && !isLoading && <span className="text-xs text-gray-400">Updating...</span>}
          </div>
          <div className="flex gap-1">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50">Previous</button>
            <span className="px-2 py-1">Page {page} of {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduleEntriesTab;
